import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Save, X, ExternalLink, Coins, Hash, DollarSign } from "lucide-react";
import { Nav, Footer } from "@/components/Chrome";
import { NeuralField } from "@/components/NeuralField";
import { getUsageModel, setUsageOverride } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const UNIT_META = {
  credits: { label: "Credits", perLabel: "credits / correction", Icon: Coins, color: "#22e58a", step: "0.1" },
  tokens: { label: "Tokens", perLabel: "tokens / correction", Icon: Hash, color: "#22d3ee", step: "100" },
  usd: { label: "USD", perLabel: "$ / correction", Icon: DollarSign, color: "#ffb000", step: "0.05" },
  tokens_and_usd: { label: "Tokens / USD", perLabel: "$ / correction", Icon: DollarSign, color: "#ffb000", step: "0.05" },
};

const sourceState = (reg, hasOverride) => {
  if (hasOverride) return { text: "Based on your usage setting", color: "#22e58a" };
  if (reg.has_baseline) return { text: "Platform baseline", color: "#22d3ee" };
  return { text: "Not enough data to estimate", color: "var(--muted)" };
};

const PlatformCard = ({ reg, override, authenticated, onSave, onClear }) => {
  const unit = reg.override_unit || reg.billing_unit;
  const meta = UNIT_META[unit] || UNIT_META.credits;
  const UnitIcon = (UNIT_META[reg.billing_unit] || meta).Icon;
  const [draft, setDraft] = useState(override?.value ?? "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(override?.value ?? ""); }, [override?.value]);

  const src = sourceState(reg, !!override);

  const save = async () => {
    const n = parseFloat(draft);
    if (Number.isNaN(n) || n <= 0) { toast.error("Enter a value greater than 0."); return; }
    setBusy(true);
    try { await onSave(reg.platform, n); toast.success(`${reg.platform} usage rate saved.`); }
    catch { toast.error("Could not save. Try again."); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true);
    try { await onClear(reg.platform); setDraft(""); toast.message(`${reg.platform} override cleared.`); }
    catch { toast.error("Could not clear. Try again."); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-[#0b0d14]/60 border border-[var(--line)] rounded-xl p-5" data-testid={`usage-card-${reg.platform}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-display font-bold text-white text-lg">{reg.platform}</p>
          <p className="font-mono text-[11px] text-[var(--muted)] mt-0.5 flex items-center gap-1.5">
            <UnitIcon size={12} style={{ color: (UNIT_META[reg.billing_unit] || meta).color }} />
            {(UNIT_META[reg.billing_unit] || meta).label} · <span className="capitalize">{reg.pricing_model}</span>
          </p>
        </div>
        <span className="font-mono text-[10px] px-2 py-1 rounded-md border shrink-0"
          style={{ color: src.color, borderColor: "var(--line)" }} data-testid={`usage-source-${reg.platform}`}>
          {src.text}
        </span>
      </div>

      {reg.documented_baseline && (
        <p className="font-mono text-[11px] text-[var(--muted)] mt-3 leading-relaxed">
          Baseline: <span className="text-white/80">{reg.documented_baseline}</span>
        </p>
      )}
      {reg.examples && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`usage-examples-${reg.platform}`}>
          {reg.examples.map((ex, i) => (
            <span key={i} className="font-mono text-[10px] text-[var(--muted)] bg-[#070910] border border-[var(--line)] rounded px-2 py-1">
              {ex.label}: {ex.credits} cr{ex.per ? `/${ex.per}` : ""}
            </span>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        {authenticated ? (
          <>
            <input
              data-testid={`usage-input-${reg.platform}`}
              type="number" min="0" step={meta.step}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Your observed rate"
              className="flex-1 bg-[#070910] border border-neon-cyan/30 rounded-lg px-3 py-2 font-mono text-sm text-white focus:outline-none focus:border-neon-cyan transition-colors"
            />
            <span className="font-mono text-[11px] text-[var(--muted)] w-24">{meta.perLabel}</span>
            <button data-testid={`usage-save-${reg.platform}`} onClick={save} disabled={busy}
              style={{ backgroundColor: "#22d3ee", color: "#05060a" }}
              className="rounded-lg font-display font-bold py-2 px-3 inline-flex items-center gap-1.5 hover:glow-cyan disabled:opacity-50 transition-shadow">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            </button>
            {override && (
              <button data-testid={`usage-clear-${reg.platform}`} onClick={clear} disabled={busy}
                className="text-[var(--muted)] hover:text-neon-amber p-2 transition-colors" aria-label="Clear override">
                <X size={16} />
              </button>
            )}
          </>
        ) : (
          <p className="font-mono text-[11px] text-[var(--muted)]">
            <Link to="/login" className="text-neon-cyan hover:underline">Sign in</Link> to set your observed rate.
          </p>
        )}
      </div>

      {reg.source_url && (
        <a href={reg.source_url} target="_blank" rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] text-[var(--muted)] hover:text-neon-cyan transition-colors">
          <ExternalLink size={10} /> {reg.source_label || "Source"}
          {reg.last_verified ? ` · verified ${reg.last_verified}` : ""}
        </a>
      )}
    </div>
  );
};

export default function Settings() {
  const { user } = useAuth();
  const [model, setModel] = useState(null);
  const [overrides, setOverrides] = useState({});

  const load = async () => {
    const d = await getUsageModel();
    setModel(d);
    setOverrides(d.overrides || {});
  };
  useEffect(() => { load().catch(() => setModel({ registry: [], authenticated: false })); }, [user]);

  const onSave = async (platform, value) => {
    const d = await setUsageOverride(platform, value);
    setOverrides(d.overrides || {});
  };
  const onClear = async (platform) => {
    const d = await setUsageOverride(platform, null);
    setOverrides(d.overrides || {});
  };

  return (
    <div className="relative text-[var(--text)] min-h-screen">
      <Nav />
      <div className="absolute top-0 inset-x-0 h-[320px] overflow-hidden pointer-events-none">
        <NeuralField className="w-full h-full opacity-70" density={55} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#05060a]" />
      </div>

      <main className="relative max-w-3xl mx-auto px-6 md:px-10 pt-28 pb-8">
        <p className="font-mono text-xs tracking-[0.3em] text-neon-cyan mb-3">// USAGE MODEL</p>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-none text-white">
          Tune your usage model
        </h1>
        <p className="font-mono text-sm text-[var(--muted)] mt-3 max-w-xl leading-relaxed">
          AI builders charge differently. CoreText uses each platform's native usage model and can improve
          estimates using the usage you actually see.
        </p>

        {model === null ? (
          <div className="flex items-center gap-3 font-mono text-sm text-neon-cyan py-20">
            <Loader2 className="animate-spin" size={18} /> Loading usage model...
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 gap-4 mt-8" data-testid="usage-model-form">
            {model.registry.map((reg) => (
              <PlatformCard
                key={reg.platform}
                reg={reg}
                override={overrides[reg.platform]}
                authenticated={!!user || model.authenticated}
                onSave={onSave}
                onClear={onClear}
              />
            ))}
          </motion.div>
        )}
      </main>
      <Footer />
    </div>
  );
}
